import React, { useState, useEffect, useMemo } from 'react';
import { useHistory, useLocation, Redirect } from 'react-router-dom';
import { Plus } from 'react-feather';
import { Helmet } from 'react-helmet';
import { Col, Row, Pagination, Input } from 'antd';
import _ from 'lodash';
import { ExclamationCircleOutlined } from '@ant-design/icons';

import { useStoreListPaginated } from 'api/owners';
import { Button, Spin, Tooltip } from 'ui-kit';
import NewStore from 'components/Dashboard/Store/StoreModal/Actions/NewStore';
import EditStore from 'components/Dashboard/Store/StoreModal/Actions/EditStore';
import DuplicateStore from 'components/Dashboard/Store/StoreModal/Actions/DuplicateStore';
import OverviewLayout from '../OverviewLayout/OverviewLayout';
import StoreListItem from 'components/StoreList/StoreListItem';
import HospitableIntroModal from './HospitableIntroModal';
import OwnerRezIntroModal from './OwnerRezIntroModal';
import GuestyIntroModal from './GuestyIntroModal';
import HostawayIntroModal from './HostawayIntroModal';
import LodgifyIntroModal from './LodgifyIntroModal';
import { formatCurrency } from 'utils';
import { useSignedUser } from 'api/auth';

import './StoreList.scss';
import { reverse } from 'utils/urls';
import { URLS } from 'components/Routes/urls';

const StoreList = () => {
  const { data: user } = useSignedUser();
  const ownerId = user?.owner_id;

  const history = useHistory();
  const { search } = useLocation();
  const { data, isLoading, currentPage, count, setPage, invalidate } = useStoreListPaginated();
  const [openDropdownId, setOpenDropdownId] = useState(-1);
  const [isNewStoreModalOpen, setIsNewStoreModalOpen] = useState(false);
  const [isDuplicateStoreModalOpen, setIsDuplicateStoreModalOpen] = useState(false);
  const [isEditStoreModalOpen, setIsEditStoreModalOpen] = useState(false);
  const [isHospitableIntroModalOpen, setIsHospitableIntroModalOpen] = useState(false);
  const [isOwnerRezIntroModalOpen, setIsOwnerRezIntroModalOpen] = useState(false);
  const [isGuestyIntroModalOpen, setIsGuestyIntroModalOpen] = useState(false);
  const [isHostawayIntroModalOpen, setIsHostawayIntroModalOpen] = useState(false);
  const [isLodgifyIntroModalOpen, setIsLodgifyIntroModalOpen] = useState(false);

  const [selectedStore, setSelectedStore] = useState(null);
  const [storesList, setStoresList] = useState([]);

  const [storeSearchInput, setStoreSearchInput] = useState('');
  const [storeSearch, setStoreSearch] = useState('');

  const sortCompare = (a, b) => {
    if (a.is_visible !== b.is_visible) {
      return a.is_visible ? -1 : 1;
    }

    if (a.store_name < b.store_name) {
      return -1;
    }

    if (a.store_name > b.store_name) {
      return 1;
    }

    return 0;
  };

  const filteredStoresList = useMemo(() => {
    const searchTerms = storeSearch.toLowerCase().trim().split(/\s+/).filter(Boolean);

    if (searchTerms.length === 0) {
      return storesList;
    }

    return storesList.filter((store) => {
      const searchableText = [store.store_name, store.city, store.postal_code]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchTerms.every((term) => searchableText.includes(term));
    });
  }, [storesList, storeSearch]);

  const handleStoreSearchChange = (event) => {
    const value = event.target.value;

    setStoreSearchInput(value);

    if (!value) {
      setStoreSearch('');
    }
  };

  const handleStoreSearch = (value) => {
    setStoreSearch(value);
  };

  const windowHandler = (ev) => {
    if (
      ['svg', 'circle', 'BUTTON'].includes(ev.target.tagName) ||
      ev.target.className === 'StoreListItem_moreDropdown dropdown'
    ) {
      return;
    }

    setOpenDropdownId(-1);
  };

  useEffect(() => {
    window.addEventListener('click', windowHandler);
    invalidate();
    return () => {
      window.removeEventListener('click', windowHandler);
    };
  }, []);

  useEffect(() => {
    if (data?.results) {
      const sorted = [...data.results].sort(sortCompare);
      setStoresList(sorted);
    }
  }, [data?.results]);

  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    const introParam = searchParams.get('intro');

    if (introParam === 'ownerrez') {
      setIsOwnerRezIntroModalOpen(true);
    }
    if (introParam === 'hospitable') {
      setIsHospitableIntroModalOpen(true);
    }
    if (introParam === 'guesty' || introParam === 'guesty_lite') {
      setIsGuestyIntroModalOpen(true);
    }
    if (introParam === 'hostaway') {
      setIsHostawayIntroModalOpen(true);
    }
    if (introParam === 'lodgify') {
      setIsLodgifyIntroModalOpen(true);
    }
    searchParams.delete('intro');
    history.replace({
      search: searchParams.toString(),
    });
  }, [search]);

  useEffect(() => {
    const nextUrl = localStorage.getItem('nextUrl');
    if (nextUrl) {
      localStorage.removeItem('nextUrl');
      history.push({
        pathname: nextUrl,
      });
    }
  }, []);

  const sumOfStoresPayouts = useMemo(
    () =>
      _.sumBy(storesList, (store) => {
        if (store.total_payout_amount_current_month < 0) {
          return 0;
        }
        return store.total_payout_amount_current_month;
      }),
    [storesList],
  );

  const currency = useMemo(() => {
    const firstStore = data?.results?.[0];
    return firstStore?.currency;
  }, [data?.results]);

  const handleNewStoreModalOpen = () => {
    setIsNewStoreModalOpen(true);
  };

  const handleNewStoreModalClose = () => {
    setIsNewStoreModalOpen(false);
  };

  const handleDuplicateStoreModalOpen = (store) => {
    setSelectedStore(store);
    setIsDuplicateStoreModalOpen(true);
  };

  const handleDuplicateStoreModalClose = () => {
    setIsDuplicateStoreModalOpen(false);
    setSelectedStore(null);
  };

  const handleEditStoreModalOpen = (store) => {
    setSelectedStore(store);
    setIsEditStoreModalOpen(true);
  };

  const handleEditStoreModalClose = () => {
    setIsEditStoreModalOpen(false);
    setSelectedStore(null);
  };

  const handleInvalidate = (storeId, currentVisible) => {
    if (storeId !== undefined) {
      const updated = storesList.map((store) =>
        store.id === storeId ? { ...store, is_visible: !currentVisible } : store,
      );
      setStoresList([...updated].sort(sortCompare));
    }

    invalidate();
  };

  if (data && !isLoading && data?.results?.length === 0) {
    return (
      <Redirect
        to={{
          pathname: URLS.ONBOARDING_VIDEO,
          state: { stores: data?.results?.length, ownerId },
        }}
      />
    );
  }

  return (
    <OverviewLayout>
      <Helmet>
        <title>Store List | The Host Co</title>
      </Helmet>
      <div className="StoreList">
        <div className="StoreList_header">
          <div className="StoreList_headerHeader">
            <div className="StoreList_title">Stores</div>
            <div className="StoreList_activities">
              <Button
                className="StoreList_addProductButton small"
                variant="contained"
                onClick={handleNewStoreModalOpen}
                startIcon={<Plus />}
              >
                Add store
              </Button>
            </div>
          </div>

          <div className="StoreList_filterBar">
            <div className="StoreList_filters">
              <div className="StoreList_filterItem StoreList_searchItem">
                <Input.Search
                  allowClear
                  value={storeSearchInput}
                  onChange={handleStoreSearchChange}
                  onSearch={handleStoreSearch}
                  placeholder="Search by store name, city or zip code"
                  enterButton
                />
              </div>
            </div>
          </div>
        </div>

        <Spin spinning={isLoading}>
          <div className="StoreList_body">
            {data &&
              !isLoading &&
              data?.results?.length > 0 &&
              (filteredStoresList.length > 0 ? (
                <div className="StoreList_table">
                  <div className="StoreList_row header_mobile">
                    Total sales this month: {formatCurrency(sumOfStoresPayouts, currency)}
                    <p className="note">/Total may not be exact due to process timing/</p>
                  </div>

                  <Row className="StoreList_row header">
                    <Col className="StoreList_col label image" span={2}>
                      {' '}
                    </Col>
                    <Col className="StoreList_col label name" span={8}>
                      Store Name
                    </Col>
                    <Col className="StoreList_col label share" span={2}>
                      Store Link
                    </Col>
                    <Col className="StoreList_col label zipCode" span={2}>
                      Zip Code
                    </Col>
                    <Col className="StoreList_col label products" span={2}>
                      Products
                    </Col>
                    <Col className="StoreList_col label orders" span={2}>
                      Orders
                      <p>(all time)</p>
                    </Col>
                    <Col className="StoreList_col label sales" span={3}>
                      <p>Payout this month</p>
                      <p>
                        (Total: {formatCurrency(sumOfStoresPayouts, currency)}){' '}
                        <Tooltip title="Total may not be exact due to process timing">
                          <ExclamationCircleOutlined />
                        </Tooltip>
                      </p>
                    </Col>
                    <Col className="StoreList_col label more" span={1}>
                      {' '}
                    </Col>
                  </Row>

                  {filteredStoresList.map((store) => (
                    <StoreListItem
                      classname="StoreList_item"
                      key={store.id}
                      store={store}
                      openDropdownId={openDropdownId}
                      setOpenDropdownId={setOpenDropdownId}
                      openDuplicateModal={handleDuplicateStoreModalOpen}
                      openEditModal={handleEditStoreModalOpen}
                      currency={store.currency}
                      onUpdate={() => handleInvalidate(store.id, store.is_visible)}
                    />
                  ))}

                  {!storeSearch && (
                    <div
                      className="StoreList_paginationContainer"
                      style={{
                        marginTop: '24px',
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      <Pagination
                        onChange={setPage}
                        current={currentPage}
                        total={count}
                        pageSize={50}
                        showSizeChanger={false}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="message">
                  <span className="message heading">No Stores Found</span>
                  <span className="message text">
                    We couldn&apos;t find any stores that matched your key words. Please try using a
                    different search term.
                  </span>
                </div>
              ))}
          </div>
        </Spin>
      </div>
      <OwnerRezIntroModal
        isOpen={isOwnerRezIntroModalOpen}
        onCancel={() => setIsOwnerRezIntroModalOpen(false)}
      />
      <HospitableIntroModal
        isOpen={isHospitableIntroModalOpen}
        onCancel={() => setIsHospitableIntroModalOpen(false)}
      />

      <GuestyIntroModal
        isOpen={isGuestyIntroModalOpen}
        onCancel={() => setIsGuestyIntroModalOpen(false)}
      />

      <HostawayIntroModal
        isOpen={isHostawayIntroModalOpen}
        onCancel={() => setIsHostawayIntroModalOpen(false)}
      />

      <LodgifyIntroModal
        isOpen={isLodgifyIntroModalOpen}
        onCancel={() => setIsLodgifyIntroModalOpen(false)}
      />

      <NewStore
        isOpen={isNewStoreModalOpen}
        onCancel={handleNewStoreModalClose}
        onSuccess={(store) =>
          history.push(
            reverse({
              url: URLS.STORE_DETAILS,
              params: { storeId: store.id },
            }),
          )
        }
      />
      <DuplicateStore
        isOpen={isDuplicateStoreModalOpen}
        store={selectedStore ? _.mapKeys(selectedStore, (v, k) => _.camelCase(k)) : null}
        onCancel={handleDuplicateStoreModalClose}
        onSuccess={(store) => {
          handleInvalidate();
          history.push(
            reverse({
              url: URLS.STORE_DETAILS,
              params: { storeId: store.id },
            }),
          );
        }}
      />
      <EditStore
        isOpen={isEditStoreModalOpen}
        store={selectedStore ? _.mapKeys(selectedStore, (v, k) => _.camelCase(k)) : null}
        onCancel={handleEditStoreModalClose}
        onSuccess={() => {
          handleInvalidate();
          handleEditStoreModalClose();
        }}
      />
    </OverviewLayout>
  );
};

export default StoreList;
